import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FadeUp, Overline, MaskedLine } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";

// Main collab worlds (these enlarge / centre when selected)
const worlds = [
  { value: "", label: "All" },
  { value: "automobile", label: "Automobile" },
  { value: "cinema", label: "Cinema" },
  { value: "wilden", label: "Wilden" },
];

// Mascot / special lines — set apart on the same line
const mascots = [
  { value: "otter", label: "Pebble The Otter" },
  { value: "originals", label: "Vincen Originals" },
];

// Third special line
const plusWorld = { value: "plus", label: "Vincen Plus" };

// Piece tabs for collab worlds (no All Pieces / Jerseys)
const collabTabs = [
  { value: "hoodie", label: "Hoodies" },
  { value: "sweatshirt", label: "Sweatshirts" },
  { value: "zipper", label: "Zippers" },
];

// Pebble The Otter line
const pebbleTabs = [
  { value: "", label: "All Pieces" },
  { value: "hoodie", label: "Hoodies" },
  { value: "sweatshirt", label: "Sweatshirts" },
  { value: "polo", label: "Polos" },
  { value: "varsity", label: "Varsity" },
];

// Vincen Originals — jerseys only
const originalsTabs = [{ value: "", label: "Jerseys" }];

// Vincen Plus line
const plusTabs = [
  { value: "", label: "All" },
  { value: "hoodie", label: "Hoodies" },
  { value: "sweatshirt", label: "Sweatshirts" },
  { value: "zipper", label: "Zippers" },
];

const HIDDEN_IN_ALL = ["otter", "varsity", "originals", "plus"];

const getTabs = (world) => {
  if (world === "otter") return pebbleTabs;
  if (world === "originals") return originalsTabs;
  if (world === "plus") return plusTabs;
  return collabTabs;
};

const Shop = () => {
  const [searchParams] = useSearchParams();
  const initial = searchParams.get("collab") || "";
  const [all, setAll] = useState(null);
  const [world, setWorld] = useState(
    [...worlds, ...mascots, plusWorld].some((w) => w.value === initial) ? initial : ""
  );
  const [sub, setSub] = useState("");

  useEffect(() => {
    api.get("/products").then((r) => setAll(r.data)).catch(() => setAll([]));
  }, []);

  const isSpecial = world === "otter" || world === "originals" || world === "plus";

  const selectWorld = (v) => {
    setWorld(v);
    setSub("");
  };

  const selectSub = (v) => {
    // collab-world tabs toggle off; special-line tabs are exclusive (All = "")
    if (!isSpecial && sub === v) setSub("");
    else setSub(v);
  };

  const products = useMemo(() => {
    if (!all) return null;
    let list = all;
    if (world === "") {
      list = list.filter((p) => !HIDDEN_IN_ALL.includes(p.collab_type));
      if (sub) list = list.filter((p) => p.category === sub);
    } else if (world === "otter") {
      if (sub === "varsity") {
        list = list.filter((p) => p.collab_type === "varsity");
      } else {
        list = list.filter((p) => p.collab_type === "otter");
        if (sub) list = list.filter((p) => p.category === sub);
        else list = list.filter((p) => p.category !== "jersey"); // All Pieces excludes jerseys
      }
    } else if (world === "originals") {
      // Vincen Originals — jerseys only
      list = list.filter((p) => p.collab_type === "originals" && p.category === "jersey");
    } else if (world === "plus") {
      list = list.filter((p) => p.collab_type === "plus");
      if (sub) list = list.filter((p) => p.category === sub);
    } else {
      list = list.filter((p) => p.collab_type === world);
      if (sub) list = list.filter((p) => p.category === sub);
    }
    return list;
  }, [all, world, sub]);

  const activeTabs = getTabs(world);

  return (
    <div data-testid="shop-page" className="min-h-screen pt-32 md:pt-40 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="text-center">
          <Overline>The Vault</Overline>
          <h1 className="mt-4 font-serif font-light text-[#F5F5F5]">
            <MaskedLine delay={0.1} className="text-5xl md:text-7xl tracking-tight">
              Collections
            </MaskedLine>
          </h1>
        </div>

        {/* WORLD SELECTOR — active enlarges & centres, others shrink */}
        <FadeUp delay={0.15} className="mt-14">
          <div className="flex flex-wrap items-baseline justify-center gap-x-7 md:gap-x-10 gap-y-3">
            <span className="lg:hidden self-center w-px h-6 md:h-8 bg-white/15" />
            {worlds.map((w) => {
              const active = world === w.value;
              return (
                <button
                  key={w.value || "all"}
                  data-testid={`filter-collab-${w.value || "all"}`}
                  onClick={() => selectWorld(w.value)}
                  style={{ transition: "font-size .55s cubic-bezier(.16,1,.3,1), color .4s ease, opacity .4s ease" }}
                  className={`font-serif italic leading-none ${
                    active
                      ? "text-gold text-4xl md:text-6xl"
                      : "text-zinc-600 hover:text-zinc-300 text-lg md:text-2xl opacity-70"
                  }`}
                >
                  {w.label}
                </button>
              );
            })}

            <span className="mx-3 md:mx-5 self-center w-px h-6 md:h-8 bg-white/15" />

            {mascots.map((w, mi) => {
              const active = world === w.value;
              return (
                <span key={w.value} className="flex items-baseline">
                  {mi > 0 && (
                    <span className="mx-3 md:mx-5 self-center w-px h-6 md:h-8 bg-white/15" />
                  )}
                  <button
                    data-testid={`filter-collab-${w.value}`}
                    onClick={() => selectWorld(w.value)}
                    style={{ transition: "font-size .55s cubic-bezier(.16,1,.3,1), color .4s ease, opacity .4s ease" }}
                    className={`font-serif italic leading-none whitespace-nowrap ${
                      active
                        ? "text-gold text-3xl md:text-5xl"
                        : "text-[#C4C9D0]/70 hover:text-[#E5E7EB] text-base md:text-xl opacity-80"
                    }`}
                  >
                    {w.label}
                  </button>
                </span>
              );
            })}

            <span className="mx-3 md:mx-5 self-center w-px h-6 md:h-8 bg-white/15" />

            {(() => {
              const active = world === plusWorld.value;
              return (
                <button
                  data-testid={`filter-collab-${plusWorld.value}`}
                  onClick={() => selectWorld(plusWorld.value)}
                  style={{ transition: "font-size .55s cubic-bezier(.16,1,.3,1), color .4s ease, opacity .4s ease" }}
                  className={`font-serif italic leading-none whitespace-nowrap ${
                    active
                      ? "text-gold text-3xl md:text-5xl"
                      : "text-[#C4C9D0]/70 hover:text-[#E5E7EB] text-base md:text-xl opacity-80"
                  }`}
                >
                  {plusWorld.label}
                </button>
              );
            })()}
            <span className="lg:hidden self-center w-px h-6 md:h-8 bg-white/15" />
          </div>
        </FadeUp>

        {/* SUB-NAV — silver tabs */}
        <FadeUp delay={0.2} className="mt-10">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8" data-testid="sub-nav">
            {activeTabs.map((t) => {
              const active = sub === t.value;
              return (
                <button
                  key={t.value || "allpieces"}
                  data-testid={`filter-sub-${t.value || "allpieces"}`}
                  onClick={() => selectSub(t.value)}
                  className={`text-xs tracking-[0.25em] uppercase transition-colors duration-300 pb-1 border-b ${
                    active
                      ? "text-[#E5E7EB] border-[#C4C9D0]"
                      : "text-[#8A8F98] border-transparent hover:text-[#C4C9D0]"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </FadeUp>

        <div className="mt-16">
          {products === null ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[3/4] bg-[#141414] border border-white/5 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <FadeUp className="py-24 text-center border border-white/5" data-testid="shop-empty">
              <p className="font-serif text-3xl md:text-4xl text-zinc-400 italic">
                The atelier is being stocked
              </p>
              <p className="mt-4 text-sm text-zinc-600 max-w-md mx-auto leading-relaxed">
                New drops are being cut as we speak. Return soon &mdash; conquest rewards the patient.
              </p>
            </FadeUp>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
